import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface MetaConfig {
  id?: number;
  name: string;
  app_id: string;
  app_secret: string;
  access_token: string;
  access_token_masked?: string;
  ad_account_id: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

const emptyConfig: MetaConfig = {
  name: 'Default',
  app_id: '',
  app_secret: '',
  access_token: '',
  ad_account_id: '',
  is_active: true,
};

const MetaAdsConfig: React.FC = () => {
  const [configs, setConfigs] = useState<MetaConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<MetaConfig | null>(null);
  const [formData, setFormData] = useState<MetaConfig>(emptyConfig);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState<any>(null);

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true, cellStyle: { color: '#6b7280', fontWeight: '500' } },
    { field: 'name', headerName: 'Config Name', sortable: true, filter: true, flex: 1, cellStyle: { fontWeight: '600', color: '#1f2937' } },
    { field: 'app_id', headerName: 'App ID', width: 160, cellStyle: { fontFamily: 'monospace', fontSize: '12px' } },
    { field: 'ad_account_id', headerName: 'Ad Account ID', width: 180, cellStyle: { fontFamily: 'monospace', fontSize: '12px', color: '#4f46e5' } },
    { field: 'access_token_masked', headerName: 'Access Token', width: 200, cellStyle: { fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' } },
    {
      field: 'is_active', headerName: 'Status', width: 100,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#d1fae5' : '#fee2e2',
          color: params.value ? '#059669' : '#dc2626',
          padding: '4px 12px', borderRadius: '12px', fontWeight: '600', fontSize: '12px'
        }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Actions', width: 140, pinned: 'right', sortable: false, filter: false,
      cellRenderer: (params: any) => {
        const btnStyle: React.CSSProperties = {
          border: 'none', borderRadius: '6px', width: '32px', height: '32px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: '16px', transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button onClick={() => handleTestConnection(params.data)} disabled={testingConnection === params.data.id}
              title="Test Connection"
              style={{ ...btnStyle, backgroundColor: testingConnection === params.data.id ? '#9ca3af' : '#3b82f6', color: 'white', opacity: testingConnection === params.data.id ? 0.6 : 1 }}
              onMouseEnter={(e) => { if (testingConnection !== params.data.id) e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              {testingConnection === params.data.id ? '⏳' : '🧪'}
            </button>
            <button onClick={() => handleEdit(params.data)} title="Edit"
              style={{ ...btnStyle, backgroundColor: '#007bff', color: 'white' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >✏️</button>
            <button onClick={() => handleDeleteClick(params.data)} title="Delete"
              style={{ ...btnStyle, backgroundColor: '#dc3545', color: 'white' }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >🗑️</button>
          </div>
        );
      }
    }
  ];

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getMetaAdsConfig();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setConfigs(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (error) {
      console.error('Failed to load Meta Ads configs:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedConfig(null);
    setFormData(emptyConfig);
    setAccountInfo(null);
    setIsFormOpen(true);
  };

  const handleEdit = (config: MetaConfig) => {
    setSelectedConfig(config);
    setFormData({ ...config, access_token: '', app_secret: '' });
    setAccountInfo(null);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (config: MetaConfig) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteMetaAdsConfig(selectedConfig.id!);
      setToast({ show: true, message: 'Configuration deleted successfully', type: 'success' });
      loadConfigs();
    } catch (error) {
      setToast({ show: true, message: 'Failed to delete configuration', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedConfig(null);
    }
  };

  const handleTestConnection = async (config: MetaConfig) => {
    try {
      setTestingConnection(config.id!);
      const response = await adminAPI.testMetaAdsConnection(config.id!);
      const data = response.data || response;
      if (data.status === 'success') {
        setToast({ show: true, message: `✅ ${data.message} — Account: ${data.account?.name}`, type: 'success' });
        setAccountInfo(data.account);
      } else {
        setToast({ show: true, message: data.message, type: 'error' });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to test connection';
      setToast({ show: true, message, type: 'error' });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSave = async () => {
    if (!formData.ad_account_id) {
      setToast({ show: true, message: 'Ad Account ID is required', type: 'error' });
      return;
    }
    if (!selectedConfig && !formData.access_token) {
      setToast({ show: true, message: 'Access Token is required', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const payload: any = { ...formData };
      // Don't send empty token on update
      if (selectedConfig?.id && !payload.access_token) delete payload.access_token;
      if (!payload.app_secret) delete payload.app_secret;

      if (selectedConfig?.id) {
        await adminAPI.updateMetaAdsConfig(selectedConfig.id, payload);
        setToast({ show: true, message: 'Configuration updated successfully', type: 'success' });
      } else {
        await adminAPI.createMetaAdsConfig(payload);
        setToast({ show: true, message: 'Configuration created successfully', type: 'success' });
      }
      setIsFormOpen(false);
      setSelectedConfig(null);
      loadConfigs();
    } catch (error: any) {
      const detail = error.response?.data;
      let msg = 'Failed to save configuration';
      if (detail && typeof detail === 'object') {
        const first = Object.values(detail).flat()[0];
        if (first) msg = String(first);
      }
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Inline form styles
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px', color: '#374151' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' };
  const helpStyle: React.CSSProperties = { fontSize: '11px', color: '#9ca3af', marginTop: '2px' };

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Meta Ads Configuration</h3>
          <p>Configure your Meta Marketing API credentials to access campaign data</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>+ Add Config</button>
          <button className="btn btn-secondary" onClick={loadConfigs}>🔄 Refresh</button>
        </div>
      </div>

      <div className="ag-grid-wrapper">
        <div className="results-info">
          <span>{configs.length}</span> configuration(s) found
        </div>
        <div className="ag-grid-container">
          <AgGridReact
            rowData={configs}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ resizable: true }}
            animateRows={true}
            pagination={true}
            paginationPageSize={10}
            loading={loading}
            domLayout="autoHeight"
          />
        </div>
      </div>

      {/* Inline Form Modal */}
      {isFormOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '520px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>
                {selectedConfig ? 'Edit Meta Ads Config' : 'New Meta Ads Config'}
              </h3>
              <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Config Name</label>
                <input style={inputStyle} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., My Store Ads" />
              </div>

              <div>
                <label style={labelStyle}>Meta App ID <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span></label>
                <input style={inputStyle} value={formData.app_id} onChange={(e) => setFormData({ ...formData, app_id: e.target.value })} placeholder="e.g., 123456789" />
                <div style={helpStyle}>From developers.facebook.com → Your App → Settings → Basic</div>
              </div>

              <div>
                <label style={labelStyle}>App Secret <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span></label>
                <input style={{ ...inputStyle }} type="password" value={formData.app_secret} onChange={(e) => setFormData({ ...formData, app_secret: e.target.value })} placeholder={selectedConfig ? '(leave blank to keep current)' : 'Enter app secret'} />
              </div>

              <div>
                <label style={labelStyle}>Ad Account ID <span style={{ color: '#dc2626' }}>*</span></label>
                <input style={inputStyle} value={formData.ad_account_id} onChange={(e) => setFormData({ ...formData, ad_account_id: e.target.value })} placeholder="e.g., act_123456789" />
                <div style={helpStyle}>Find in Meta Business Manager → Ad Accounts. Include "act_" prefix.</div>
              </div>

              <div>
                <label style={labelStyle}>Access Token <span style={{ color: '#dc2626' }}>*</span></label>
                <textarea style={{ ...inputStyle, minHeight: '80px', fontFamily: 'monospace', fontSize: '12px' }} value={formData.access_token} onChange={(e) => setFormData({ ...formData, access_token: e.target.value })} placeholder={selectedConfig ? '(leave blank to keep current)' : 'Paste your long-lived access token here'} />
                <div style={helpStyle}>Generate from Graph API Explorer with ads_read permission. Use a System User token for production.</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                <label htmlFor="is_active" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Active</label>
              </div>

              {accountInfo && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px' }}>✅ Connected Account</div>
                  <div style={{ fontSize: '13px', color: '#15803d' }}>
                    <div><strong>Name:</strong> {accountInfo.name}</div>
                    <div><strong>Status:</strong> {accountInfo.status}</div>
                    <div><strong>Currency:</strong> {accountInfo.currency}</div>
                    <div><strong>Timezone:</strong> {accountInfo.timezone}</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => setIsFormOpen(false)} style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: saving ? '#93c5fd' : '#2563eb', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}>
                {saving ? 'Saving...' : (selectedConfig ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Meta Ads Configuration"
        message={`Are you sure you want to delete "${selectedConfig?.name}"? This will disconnect your Meta Ads account.`}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Notification */}
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default MetaAdsConfig;
